import {
  type DateValue,
  endOfMonth,
  endOfWeek,
  getLocalTimeZone,
  parseDate,
  startOfMonth,
  startOfWeek,
  today,
} from "@internationalized/date";
import { useNavigate } from "@tanstack/react-router";
import type { Metric } from "@unbox/api";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDateLimits } from "@/api/date-limits";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import {
  MetricsConfigurationForm,
  type MetricsConfigurationFormHandle,
  type MetricsFormData,
} from "@/components/MetricsConfigurationForm";
import { getFilteredMetricsArray, METRICS_MAP } from "@/constants/metrics";
import { REPORT_TEMPLATES } from "@/constants/reportTemplates";
import { cx } from "@/utils/cx";
import type { ReportTemplate } from "../types";
import { CustomReportDateRangeSelector } from "./CustomReportDateRangeSelector";
import { TemplateFormConfiguration } from "./TemplateFormConfiguration";

interface ReportConfigurationModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    name: string;
    layout: "chart" | "table" | "bar";
    metrics: Metric[];
    templateId?: string | null;
    startDate?: string;
    endDate?: string;
  }) => Promise<{ id: string } | null>;
  isCreating?: boolean;
  editMode?: boolean;
  existingReport?: {
    id: string;
    name: string;
    layout: "chart" | "table" | "bar";
    metrics: Metric[];
    templateId?: string | null;
    startDate?: string | null;
    endDate?: string | null;
  };
}

// Custom name generator that includes date suffix for reports
const generateReportName = ({
  layout: format,
  chartMetrics,
  tableMetrics,
  dateRange,
}: {
  layout: "chart" | "table" | "bar";
  chartMetrics: string[];
  tableMetrics: string[];
  dateRange?: { start: DateValue; end: DateValue } | null;
}) => {
  const validMetrics = format === "chart" ? chartMetrics.filter((m) => m !== "") : tableMetrics.filter((m) => m !== "");

  if (validMetrics.length === 0) return "";

  const metricLabels = validMetrics.map((m) => METRICS_MAP[m as Metric]?.label || m);

  // Use date range if provided, otherwise use today's date
  let dateStr: string;
  if (dateRange) {
    const startDateStr = dateRange.start.toDate(getLocalTimeZone()).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const endDateStr = dateRange.end.toDate(getLocalTimeZone()).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    dateStr = `${startDateStr} - ${endDateStr}`;
  } else {
    const today = new Date();
    dateStr = today.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  if (metricLabels.length <= 2) {
    return `${metricLabels.join(", ")} (${dateStr})`;
  }
  return `${metricLabels.slice(0, 2).join(", ")}, ${metricLabels.length - 2} more (${dateStr})`;
};

export const ReportConfigurationModal: React.FC<ReportConfigurationModalProps> = ({
  isOpen,
  onOpenChange,
  onSave,
  isCreating = false,
  editMode = false,
  existingReport,
}) => {
  const navigate = useNavigate();
  const metricsFormRef = useRef<MetricsConfigurationFormHandle>(null);

  // Track custom form validity reactively so the button disabled state never stales
  const [isCustomFormValid, setIsCustomFormValid] = useState(() => {
    if (editMode && existingReport && !existingReport.templateId) {
      return existingReport.metrics.length > 0 && !!existingReport.name.trim();
    }
    return false;
  });

  // Check TikTok Ads connection status to filter Ads metrics
  const filteredMetrics = useMemo(() => getFilteredMetricsArray(), []);

  // Date limits for restricting selectable dates
  const { data: dateLimits } = useDateLimits();
  const limits = useMemo(() => {
    if (!dateLimits) return undefined;
    return {
      minDate: parseDate(dateLimits.minDate),
      maxDate: parseDate(dateLimits.maxDate),
    };
  }, [dateLimits]);

  // Tab state - show appropriate tab based on report type when editing
  const [activeTab, setActiveTab] = useState<"template" | "custom">(
    editMode && existingReport ? (existingReport.templateId ? "template" : "custom") : "template",
  );

  // Template tab state - no default selection unless editing an existing template report
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(() => {
    if (editMode && existingReport?.templateId) {
      return REPORT_TEMPLATES.find((t) => t.id === existingReport.templateId) || null;
    }
    return null;
  });

  // Initialize time period and date range based on what's available
  const getInitialState = () => {
    // If editing and has date range, use that
    if (editMode && existingReport?.startDate && existingReport?.endDate) {
      const existingStart = parseDate(existingReport.startDate.split("T")[0]);
      const existingEnd = parseDate(existingReport.endDate.split("T")[0]);
      return { period: "specificRange" as const, range: { start: existingStart, end: existingEnd } };
    }

    const now = today(getLocalTimeZone());

    // Try last month first
    const lastMonthStart = startOfMonth(now).subtract({ months: 1 });
    const lastMonthEnd = endOfMonth(now).subtract({ months: 1 });
    const lastMonthRange = { start: lastMonthStart, end: lastMonthEnd };

    // Check if last month is within limits
    if (!limits || (lastMonthStart.compare(limits.minDate) >= 0 && lastMonthEnd.compare(limits.maxDate) <= 0)) {
      return { period: "lastMonth" as const, range: lastMonthRange };
    }

    // Try last week
    const lastWeekStart = startOfWeek(now, "en-US").subtract({ weeks: 1 });
    const lastWeekEnd = endOfWeek(now, "en-US").subtract({ weeks: 1 });
    const lastWeekRange = { start: lastWeekStart, end: lastWeekEnd };

    if (!limits || (lastWeekStart.compare(limits.minDate) >= 0 && lastWeekEnd.compare(limits.maxDate) <= 0)) {
      return { period: "lastWeek" as const, range: lastWeekRange };
    }

    // Default to specific range if neither preset is available
    // Use the full available date range or default to today
    if (limits) {
      return { period: "specificRange" as const, range: { start: limits.minDate, end: limits.maxDate } };
    }

    return { period: "lastMonth" as const, range: lastMonthRange };
  };

  const initialState = getInitialState();
  const [timePeriod, setTimePeriod] = useState<"lastMonth" | "lastWeek" | "specificRange">(initialState.period);
  const [dateRange, setDateRange] = useState<{ start: DateValue; end: DateValue } | null>(initialState.range);
  const [reportName, setReportName] = useState(editMode && existingReport ? existingReport.name : "");

  // Custom report date range state (separate from template date range)
  const [customTimePeriod, setCustomTimePeriod] = useState<"lastMonth" | "lastWeek" | "specificRange">(
    initialState.period,
  );
  const [customDateRange, setCustomDateRange] = useState<{ start: DateValue; end: DateValue } | null>(
    initialState.range,
  );

  // Update report name when template is selected or date range changes (but not in edit mode)
  useEffect(() => {
    if (selectedTemplate && !editMode && dateRange) {
      const startDateStr = dateRange.start.toDate(getLocalTimeZone()).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const endDateStr = dateRange.end.toDate(getLocalTimeZone()).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      setReportName(`${selectedTemplate.name} (${startDateStr} - ${endDateStr})`);
    } else if (selectedTemplate && !editMode) {
      setReportName(selectedTemplate.name);
    }
  }, [selectedTemplate, editMode, dateRange]);

  // Update initial state when limits are loaded
  useEffect(() => {
    if (limits) {
      const now = today(getLocalTimeZone());

      // Check if current selection is still valid
      if (timePeriod === "lastMonth") {
        const lastMonthStart = startOfMonth(now).subtract({ months: 1 });
        const lastMonthEnd = endOfMonth(now).subtract({ months: 1 });
        const isValid = lastMonthStart.compare(limits.minDate) >= 0 && lastMonthEnd.compare(limits.maxDate) <= 0;

        if (!isValid) {
          // Try last week
          const lastWeekStart = startOfWeek(now, "en-US").subtract({ weeks: 1 });
          const lastWeekEnd = endOfWeek(now, "en-US").subtract({ weeks: 1 });
          const isLastWeekValid =
            lastWeekStart.compare(limits.minDate) >= 0 && lastWeekEnd.compare(limits.maxDate) <= 0;

          if (isLastWeekValid) {
            setTimePeriod("lastWeek");
            setDateRange({ start: lastWeekStart, end: lastWeekEnd });
          } else {
            // Default to specific range with full available range
            setTimePeriod("specificRange");
            setDateRange({ start: limits.minDate, end: limits.maxDate });
          }
        }
      } else if (timePeriod === "lastWeek") {
        const lastWeekStart = startOfWeek(now, "en-US").subtract({ weeks: 1 });
        const lastWeekEnd = endOfWeek(now, "en-US").subtract({ weeks: 1 });
        const isValid = lastWeekStart.compare(limits.minDate) >= 0 && lastWeekEnd.compare(limits.maxDate) <= 0;

        if (!isValid) {
          // Default to specific range with full available range
          setTimePeriod("specificRange");
          setDateRange({ start: limits.minDate, end: limits.maxDate });
        }
      }
    }
  }, [limits]);

  const handleTemplateSubmit = async () => {
    if (!selectedTemplate || !reportName.trim() || !dateRange) {
      return;
    }

    // Convert CalendarDate to ISO string for backend
    const startDate = dateRange.start.toDate(getLocalTimeZone()).toISOString();
    const endDate = dateRange.end.toDate(getLocalTimeZone()).toISOString();

    const result = await onSave({
      name: reportName,
      layout: selectedTemplate.layout,
      metrics: selectedTemplate.metrics,
      templateId: selectedTemplate.id,
      startDate,
      endDate,
    });

    if (result?.id) {
      navigate({ to: `/reports/${result.id}` });
    }
  };

  const handleCustomSubmit = async (data: MetricsFormData) => {
    const result = await onSave({
      name: data.name,
      layout: data.layout,
      metrics: data.metrics as Metric[],
      templateId: null, // Explicitly set to null to clear any existing templateId
      startDate: customDateRange ? customDateRange.start.toDate(getLocalTimeZone()).toISOString() : undefined,
      endDate: customDateRange ? customDateRange.end.toDate(getLocalTimeZone()).toISOString() : undefined,
    });

    if (result?.id) {
      navigate({ to: `/reports/${result.id}` });
    }

    return result;
  };

  // Set up initialData for MetricsConfigurationModal
  const initialData = existingReport
    ? {
        id: existingReport.id,
        name: existingReport.name,
        layout: existingReport.layout as "chart" | "table" | "bar",
        metrics: existingReport.metrics,
      }
    : undefined;

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable className="sm:items-start sm:pt-20">
      <Modal className="max-w-[548px]">
        <Dialog className="flex flex-col outline-none bg-primary rounded-2xl shadow-xl">
          {/* Modal Header */}
          <div className="flex flex-col items-center w-full">
            <div className="flex flex-col gap-4 items-start pb-0 pt-6 px-6 w-full">
              <div className="flex flex-col gap-0.5 items-start w-full">
                <h2 className="font-semibold text-md text-primary leading-6 w-full">
                  {editMode ? "Edit Report" : "New Report"}
                </h2>
              </div>
            </div>
            <CloseButton className="absolute right-3 top-3" onPress={() => onOpenChange(false)} />
            <div className="h-5 w-full" />
          </div>

          {/* Modal Content */}
          <div className="flex flex-col gap-6 items-start p-6 w-full">
            {/* Tab Switcher */}
            <div className="self-stretch p-1 bg-primary rounded-[10px] outline outline-1 outline-offset-[-1px] outline-secondary inline-flex justify-center items-center gap-1">
              <button
                type="button"
                onClick={() => setActiveTab("template")}
                className={cx(
                  "flex-1 h-9 px-3 py-2 rounded-md flex justify-center items-center gap-2 overflow-hidden transition-colors",
                  activeTab === "template" ? "bg-fg-primary text-white" : "text-quaternary hover:text-secondary",
                )}
              >
                <div className="justify-start text-sm font-semibold leading-5">Start with Template</div>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("custom")}
                className={cx(
                  "flex-1 h-9 px-3 py-2 rounded-md flex justify-center items-center gap-2 overflow-hidden transition-colors",
                  activeTab === "custom" ? "bg-fg-primary text-white" : "text-quaternary hover:text-secondary",
                )}
              >
                <div className="justify-start text-sm font-semibold leading-5">Custom Report</div>
              </button>
            </div>

            {/* Template Tab Content */}
            {activeTab === "template" && (
              <TemplateFormConfiguration
                selectedTemplate={selectedTemplate}
                onTemplateSelect={setSelectedTemplate}
                reportName={reportName}
                onReportNameChange={setReportName}
                timePeriod={timePeriod}
                onTimePeriodChange={setTimePeriod}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                limits={limits}
              />
            )}

            {/* Custom Tab Content */}
            {activeTab === "custom" && (
              <div className="flex flex-col gap-6 w-full">
                {/* Metrics Configuration Form */}
                <MetricsConfigurationForm
                  ref={metricsFormRef}
                  onSubmit={handleCustomSubmit}
                  initialData={editMode && !existingReport?.templateId ? initialData : undefined}
                  isEditMode={editMode && !existingReport?.templateId}
                  isSubmitting={isCreating}
                  generateSuggestedName={generateReportName}
                  nameLabel="Report name"
                  dateRange={customDateRange}
                  filteredMetrics={filteredMetrics}
                  onValidityChange={setIsCustomFormValid}
                  dateRangeSlot={
                    <CustomReportDateRangeSelector
                      timePeriod={customTimePeriod}
                      onTimePeriodChange={setCustomTimePeriod}
                      dateRange={customDateRange}
                      onDateRangeChange={setCustomDateRange}
                      limits={limits}
                    />
                  }
                />
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex flex-col items-start justify-center pb-0 pt-8 w-full">
            <div className="flex gap-3 items-center pb-6 pt-0 px-6 w-full">
              <div className="flex gap-3 items-center justify-end flex-1">
                <Button color="secondary" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={activeTab === "template" ? handleTemplateSubmit : () => metricsFormRef.current?.submit()}
                  disabled={
                    activeTab === "template"
                      ? !selectedTemplate || !reportName.trim() || !dateRange || isCreating
                      : !isCustomFormValid || isCreating
                  }
                >
                  {isCreating
                    ? editMode
                      ? "Updating..."
                      : "Creating..."
                    : editMode
                      ? "Update Report"
                      : "Create Report"}
                </Button>
              </div>
            </div>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
